import {Component, Input, OnDestroy, OnInit, inject, computed} from '@angular/core';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CoreService} from '../../../core/services/core.service';
import {DraftsService} from '../../../core/services/drafts.service';
import {CoreEventsService} from '../../../core/services/core-events.service';
import {Comment, Patient, PracticeCommentTemplate, CommentText, UserSettings} from '@xmedical/core';
import {ClinicalNoteDownloadRequestedEvent} from '@xmedical/shared';
import {FileUploaderService} from '@xmedical/uploads';
import {PatientCommentsStore, PatientDraftsStore, PatientStore, PracticeStore, UserStore} from '../../../core/state';


@Component({
    selector: 'app-patient-comments-widget',
    templateUrl: './comments-widget.component.html',
    styleUrls: ['./comments-widget.component.scss'],
    standalone: false
})
export class CommentsWidgetComponent implements OnInit, OnDestroy {

  private coreEventService = inject(CoreEventsService);
  private coreService = inject(CoreService);
  private draftService = inject(DraftsService);
  private fileUploaderService = inject(FileUploaderService);
  private userStore = inject(UserStore);
  private patientStore = inject(PatientStore);
  private practiceStore = inject(PracticeStore);
  private draftsStore = inject(PatientDraftsStore);
  protected commentsStore = inject(PatientCommentsStore);

  readonly currentUserId = computed(() => this.userStore.userId());
  readonly rollbackEnabled = computed(() => this.practiceStore.practiceSettings()?.rollbackToDraftEnabled ?? false);
  readonly rollbackWindowMinutes = computed(() => this.practiceStore.practiceSettings()?.rollbackToDraftWindowMinutes ?? 0);

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasComments(): boolean {
    return this.comments().length > 0;
  }

  get patientId(): number {
    return this.patientStore.current()?.id ?? 0;
  }

  get patient(): Patient | null {
    return this.patientStore.current();
  }

  private subscriptions: Subscription[] = [];
  private currentComment: Comment = null;
  error = '';
  settings: UserSettings;
  commentTemplates: PracticeCommentTemplate[] = [];
  templates: PracticeCommentTemplate[] = [];

  @Input() title = 'Comments';
  @Input() types: number[] = [1];

  /** Comments for this widget's configured types (filtered slice of the store). */
  readonly comments = computed(() =>
    this.commentsStore.items().filter(c => this.types.includes(c.commentType?.id))
  );
  readonly loading = computed(() => this.commentsStore.loading());

  canWrite = computed(() => this.coreService.hasPermission('Clinical.Comments.Write'));

  get shouldShowDropdown(): boolean {
    const settings = this.userStore.settings();
    const limit = settings?.commentButtonOverflowLimit;
    if (limit == null || limit <= 0) {
      return false;
    }
    return this.templates.length > limit;
  }

  private static replaceChars(string: string, find: string, replace: string): string {
    const regExp = RegExp(CommentsWidgetComponent.escapeRegExp(find), 'g');
    return string.replace(regExp, replace).trim();
  }

  private static escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
    this.commentTemplates = this.coreService.commentTemplates();
    this.subscriptions.push(this.coreEventService.events$.pipe(filter(event => event.type === 'comment_templates_loaded')).subscribe(event => {
      if (event.type === 'comment_templates_loaded') {
        this.commentTemplates = this.coreService.commentTemplates();
        this.setTemplates();
      }
    }));
    this.setTemplates();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  selectTemplate(templateId: number): void {
    const commentTemplate = this.commentTemplates.find(c => c.id === templateId);
    this.createComment(commentTemplate);
  }

  private setTemplates() {
    if (this.commentTemplates && this.commentTemplates.length > 0) {
      this.templates = this.commentTemplates.filter(t => this.types.includes(t.type?.id)).sort((a, b) => a.type.priority > b.type.priority ? 1 : -1);
    }
  }

  createComment(commentTemplate: PracticeCommentTemplate) {
    if (!this.patient) return;
    this.currentComment = this.createNewComment(commentTemplate);
    const patient = this.patient;
    this.subscriptions.push(this.commentsStore.create(this.currentComment).subscribe({
      next: commentId => {
        this.currentComment = null;
        this.draftService.addNewDraft(patient.id, patient.profileId, patient.practiceId, 'comment', commentId, 'New Comment');
      },
      error: () => {
        this.coreService.showErrorToast('Could not create new comment');
      }
    }));
  }

  private createNewComment(commentTemplate: PracticeCommentTemplate): Comment {
    return {
      id: null,
      typeId: commentTemplate.type.id,
      templateId: commentTemplate.id,
      commentType: commentTemplate.type,
      commentTemplate: commentTemplate,
      comment: {
        text: commentTemplate.commentTemplate.text,
        template: commentTemplate.commentTemplate
      } as CommentText,
      locked: false,
      patientId: this.patient!.id,
      practiceId: this.coreService.selectedPracticeId(),
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdUser: this.coreService.currentUser,
      lastModifiedUser: this.coreService.currentUser,
      deleted: false,
    } as Comment;
  }

  onUnlockRequested(event: {commentId: number}): void {
    this.subscriptions.push(this.commentsStore.unlock(event.commentId).subscribe({
      next: success => {
        if (success) this.draftsStore.reload();
      },
      error: () => this.coreService.showErrorToast('Could not rollback this comment to draft')
    }));
  }

  onDownloadRequested(event: ClinicalNoteDownloadRequestedEvent): void {
    const upload = event.upload;
    const getUrlFn = upload.legacy
      ? this.fileUploaderService.getSignedUrl(upload.path, 'GET', 300)
      : this.fileUploaderService.getSignedUploadsUrl(upload.path, 'GET', 300);

    getUrlFn.subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: (err) => {
        this.coreService.showErrorToast('Could not download attachment');
        console.error('Failed to get download URL:', err);
      }
    });
  }
}
